// src/components/admin/AdminNavbar.tsx
import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaHome, FaUserMd, FaUsers, FaClipboardList, FaBars, FaTimes, FaPills, FaCalendarAlt, FaCog, FaUserFriends, FaSignOutAlt } from 'react-icons/fa';
import AdminChangePasswordDialog from '../../components/AdminChangePasswordDialog';
import classNames from 'classnames';
import { motion, AnimatePresence } from 'framer-motion';

/** ====== Portal 下拉菜单：渲染到 body，跟随锚点定位 ====== */
const SettingsMenuPortal: React.FC<{
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  width?: number; // 可固定宽度，默认 224px（w-56）
}> = ({ anchorEl, open, onClose, children, width = 224 }) => {
  const menuRef = useRef<HTMLDivElement>(null);

  // 计算并设置菜单位置（按钮右对齐，按钮下方）
  const positionMenu = () => {
    const el = menuRef.current;
    if (!el || !anchorEl) return;
    const rect = anchorEl.getBoundingClientRect();

    // 优先放在按钮下方；若空间不足则向上“翻转”
    const margin = 8;
    const menuHeight = el.offsetHeight || 0;
    const spaceBelow = window.innerHeight - rect.bottom;
    const placeAbove = spaceBelow < menuHeight + margin;

    const top = placeAbove ? rect.top + window.scrollY - menuHeight - margin : rect.bottom + window.scrollY + margin;
    // 右对齐：菜单右边对齐按钮右边，且不越出视口
    const left = Math.min(
      rect.right + window.scrollX - width,
      window.scrollX + document.documentElement.clientWidth - width - margin
    );

    el.style.position = 'absolute';
    el.style.top = `${Math.max(top, window.scrollY + margin)}px`;
    el.style.left = `${Math.max(left, window.scrollX + margin)}px`;
    el.style.width = `${width}px`;
    el.style.zIndex = '9999';
  };

  // 初次和每次 open/anchor 变化时定位
  useLayoutEffect(() => {
    if (open) {
      positionMenu();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, anchorEl]);

  // 滚动/缩放/窗口大小改变时重定位
  useEffect(() => {
    if (!open) return;
    const reflow = () => positionMenu();
    window.addEventListener('scroll', reflow, true);
    window.addEventListener('resize', reflow);
    return () => {
      window.removeEventListener('scroll', reflow, true);
      window.removeEventListener('resize', reflow);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, anchorEl]);

  // 外点关闭
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target)) return;
      if (anchorEl?.contains(target)) return;
      onClose();
    };
    document.addEventListener('mousedown', onDocClick, true);
    return () => document.removeEventListener('mousedown', onDocClick, true);
  }, [open, onClose, anchorEl]);

  if (!open) return null;
  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      className="rounded-xl border border-gray-200 bg-white shadow-lg p-2 max-h-[70vh] overflow-auto"
      style={{ boxShadow: '0 10px 30px rgba(0,0,0,0.12)' }}
    >
      {children}
    </div>,
    document.body
  );
};

const AdminNavbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'ja';

  const [menuOpen, setMenuOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showPwdDlg, setShowPwdDlg] = useState(false);
  const [openSettings, setOpenSettings] = useState(false);

  const burgerRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const settingsBtnRef = useRef<HTMLButtonElement>(null);

  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    setShowLogoutConfirm(false);
    navigate('/login/admin');
  };

  const handleLanguageChange = (lang: 'ja' | 'zh' | 'en') => {
    i18n.changeLanguage(lang);
  };

  const menuItems = [
    { key: 'dashboard', label: t('admin.menu.dashboard'), icon: <FaHome />, path: '/admin' },
    { key: 'doctors', label: t('admin.menu.doctors'), icon: <FaUserMd />, path: '/admin/doctors' },
    { key: 'users', label: t('admin.menu.users'), icon: <FaUsers />, path: '/admin/users' },
    { key: 'reservations', label: t('admin.menu.reservations'), icon: <FaClipboardList />, path: '/admin/reservations' },
    { key: 'medicines', label: t('admin.menu.medicines'), icon: <FaPills />, path: '/admin/medicines' },
    { key: 'schedules', label: t('admin.schedules.title'), icon: <FaCalendarAlt />, path: '/admin/schedules' },
  ];

  // 关闭移动菜单（外点）
  // ※ ドロップダウン本体は burgerRef の外（兄弟要素）にあるため、
  //   burgerRef だけを見ると項目クリックの mousedown が「外側クリック」と
  //   誤判定され、click(=navigate)が発火する前にメニューが閉じてしまう。
  //   mobileMenuRef も合わせてチェックする。
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (burgerRef.current?.contains(target)) return;
      if (mobileMenuRef.current?.contains(target)) return;
      setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <>
      {/* 固定ヘッダー（保持原样即可，overflow 不再影响下拉） */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-background/90 backdrop-blur-md border-b border-border/40 h-16">
        <div className="mx-auto max-w-screen-xl px-4 h-full">
          <div className="flex items-center h-full">
            {/* 左：ロゴ */}
            <div className="flex items-center gap-3 min-w-0">
              <img
                src="/images/Melodislogo.png"
                alt="Logo"
                className="h-8 w-auto cursor-pointer"
                onClick={() => navigate('/admin')}
              />
              <h1 className="hidden sm:block font-bold tracking-tight text-gray-900 whitespace-nowrap text-lg">
                {t('admin.title')}
              </h1>
            </div>

            {/* 中央：ナビ */}
            <nav
              className={classNames(
                'hidden md:flex flex-1 justify-center items-center gap-1',
                'overflow-hidden'
              )}
              aria-label="Admin navigation"
            >
              <div className="flex items-center gap-1 max-w-full overflow-hidden">
                {menuItems.map((item) => {
                  const active = location.pathname === item.path;
                  return (
                    <button
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      title={item.label}
                      className={classNames(
                        'flex items-center rounded-full px-3 py-2 transition-all duration-200',
                        active 
                          ? 'bg-coral text-white shadow-soft font-bold translate-y-[-1px]' 
                          : 'text-gray-600 hover:text-coral hover:bg-coral/5'
                      )}
                    >
                      <span className="text-lg">{item.icon}</span>
                      <span className="ml-2 hidden lg:inline text-sm font-medium whitespace-nowrap">
                        {item.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </nav>

            {/* 右：設定（デスクトップ） */}
            <div className="hidden md:flex items-center gap-3 min-w-0">
              <span className="text-sm text-gray-700 truncate">{t('admin.welcome')}</span>

              {/* 齿轮按钮只是锚点，菜单用 Portal 渲染到 body */}
              <button
                ref={settingsBtnRef}
                onClick={() => setOpenSettings((s) => !s)}
                aria-haspopup="menu"
                aria-expanded={openSettings}
                className="p-2 rounded-full hover:bg-coral/10 text-gray-600 transition-colors"
                title={t('admin.tab_settings')}
              >
                <FaCog size={18} />
              </button>
            </div>

            {/* 右：モバイル（ハンバーガー） */}
            <div className="md:hidden ml-auto" ref={burgerRef}>
              <button
                onClick={() => setMenuOpen((s) => !s)}
                className="text-gray-700 p-2 rounded hover:bg-gray-100"
                aria-label="Open menu"
                aria-expanded={menuOpen}
              >
                {menuOpen ? <FaTimes size={22} /> : <FaBars size={22} />}
              </button>
            </div>
          </div>
        </div>

        {/* モバイルメニュー（仍在 header 内，OK） */}
        {menuOpen && (
          <div className="md:hidden" ref={mobileMenuRef}>
            <div className="mx-auto max-w-screen-xl px-4 pb-3">
              <div className="rounded-xl border border-gray-200 bg-white shadow-lg p-3 z-50 max-h-[70vh] overflow-auto">
                <div className="flex flex-col gap-1">
                  {menuItems.map((item) => {
                    const active = location.pathname === item.path;
                    return (
                      <button
                        key={item.path}
                        onClick={() => {
                          setMenuOpen(false);
                          navigate(item.path);
                        }}
                        className={classNames(
                          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm whitespace-nowrap',
                          active ? 'bg-coral-light text-coral font-semibold' : 'text-gray-700 hover:bg-gray-100'
                        )}
                      >
                        <span className="text-base">{item.icon}</span>
                        <span className="truncate">{item.label}</span>
                      </button>
                    );
                  })}

                  <hr className="my-2" />

                  {/* 言語切替（モバイル） */}
                  <div className="flex items-center justify-between gap-3">
                    <label className="text-sm text-gray-600 whitespace-nowrap">
                      {t('admin.language') || 'Language'}
                    </label>
                    <select
                      value={i18n.language}
                      onChange={(e) => handleLanguageChange(e.target.value as 'ja' | 'zh' | 'en')}
                      className="text-sm border rounded px-2 py-1 bg-white flex-1"
                    >
                      <option value="ja">日本語</option>
                      <option value="zh">中文</option>
                      {/* <option value="en">English</option> */}
                    </select>
                  </div>

                  {/* パスワード変更（モバイル） */}
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      setShowPwdDlg(true);
                    }}
                    className="mt-1 w-full text-left rounded-lg px-3 py-2 text-sm hover:bg-gray-100 text-gray-700 whitespace-nowrap"
                  >
                    {t('password.change')}
                  </button>

                  {/* ログアウト（モバイル） */}
                  <button
                    onClick={handleLogout}
                    className="w-full text-left rounded-lg px-3 py-2 text-sm bg-red-600 text-white hover:bg-red-700 whitespace-nowrap"
                  >
                    {t('admin.logout') || t('logout') || 'Logout'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* 固定ヘッダー分の空き */}
      <div className="h-16" />

      {/* 密码修改弹窗 */}
      <AdminChangePasswordDialog
        open={showPwdDlg}
        onClose={() => setShowPwdDlg(false)}
        onSuccess={() => setShowPwdDlg(false)}
      />

      {/* ====== 设置菜单：通过 Portal 渲染到 body，不会被裁剪 ====== */}
      <SettingsMenuPortal
        anchorEl={settingsBtnRef.current}
        open={openSettings}
        onClose={() => setOpenSettings(false)}
        width={224} // w-56
      >
        {/* 言語切替 */}
        <div className="px-2 py-2">
          <label className="block text-xs text-gray-500 mb-1">
            {t('admin.language') || 'Language'}
          </label>
          <select
            value={i18n.language}
            onChange={(e) => {
              handleLanguageChange(e.target.value as 'ja' | 'zh' | 'en');
            }}
            className="w-full text-sm border rounded-lg px-3 py-2 bg-white"
          >
            <option value="ja">日本語</option>
            <option value="zh">中文</option>
            {/* <option value="en">English</option> */}
          </select>
        </div>

        <hr className="my-1" />

        <button
          role="menuitem"
          onClick={() => {
            setOpenSettings(false);
            setShowPwdDlg(true);
          }}
          className="w-full text-left rounded-lg px-3 py-2 text-sm hover:bg-gray-100 text-gray-700"
        >
          {t('password.change')}
        </button>

        <button
          role="menuitem"
          onClick={() => {
            setOpenSettings(false);
            handleLogout();
          }}
          className="w-full text-left rounded-lg px-3 py-2 text-sm bg-red-600 text-white hover:bg-red-700"
        >
          {t('admin.logout') || t('logout') || 'Logout'}
        </button>
      </SettingsMenuPortal>

      {/* Admin Logout Confirmation Dialog */}
      <AnimatePresence>
        {showLogoutConfirm && (
          <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowLogoutConfirm(false)}
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              className="relative w-full max-w-xs overflow-hidden rounded-3xl bg-white p-6 shadow-2xl text-center"
            >
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-50 text-red-500">
                <FaSignOutAlt size={24} />
              </div>
              <h3 className="mb-2 text-lg font-bold text-gray-900">
                {t('admin.confirm_logout') || "Confirm Logout"}
              </h3>
              <p className="mb-6 text-sm text-gray-500">
                {t('admin.confirm_logout_desc')}
              </p>
              <div className="flex flex-col gap-2">
                <button
                  onClick={confirmLogout}
                  className="w-full rounded-xl bg-red-600 py-3 text-sm font-bold text-white shadow-md transition-all active:scale-95 hover:bg-red-700"
                >
                  {t('admin.logout') || "Logout"}
                </button>
                <button
                  onClick={() => setShowLogoutConfirm(false)}
                  className="w-full rounded-xl border border-gray-100 bg-gray-50 py-3 text-sm font-bold text-gray-500 transition-all active:scale-95"
                >
                  {t('common.cancel') || "Cancel"}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AdminNavbar;
